import { Button, InventoryNumber, StatusDot } from '@artosera/ui';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreerEditionsOeuvre, useEditionsOeuvre } from '../../../api/oeuvres.js';
import { couleurDuStatut } from '../statutUi.js';
import styles from './Tabs.module.css';

export interface EditionsTabProps {
  galerieSlug: string;
  oeuvreId: string;
  /** Droit d'écriture (`artworks:write`) : sans lui, pas de bouton de création (ARTO-53, § 4). */
  peutEcrire: boolean;
}

/** Éditions (3.3, écran 2, 4.2) : exemplaires suivis un par un, ou simple compteur. */
export function EditionsTab({ galerieSlug, oeuvreId, peutEcrire }: EditionsTabProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'oeuvres']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const editions = useEditionsOeuvre(galerieSlug, oeuvreId);
  const creer = useCreerEditionsOeuvre(galerieSlug);

  if (editions.isLoading) return <p className={styles.vide}>…</p>;

  if (editions.data?.type !== 'edition') {
    return <p className={styles.vide}>{t('regie:oeuvres.editions.aucunTirage')}</p>;
  }

  const { compteur, editions: exemplaires } = editions.data;

  return (
    <div className={styles.formulaire}>
      <div className={styles.compteur}>
        <div>
          <strong>{compteur.total}</strong>
          <span>{t('oeuvres:champs.tirage')}</span>
        </div>
        <div>
          <strong>{compteur.vendus}</strong>
          <span>{t('oeuvres:statuts.vendue')}</span>
        </div>
        <div>
          <strong>{compteur.disponibles}</strong>
          <span>{t('oeuvres:statuts.disponible')}</span>
        </div>
      </div>

      {exemplaires.length === 0 ? (
        peutEcrire ? (
          <Button
            variant="secondary"
            onClick={() => creer.mutate(oeuvreId)}
            loading={creer.isPending}
          >
            {t('regie:oeuvres.editions.creerExemplaires')}
          </Button>
        ) : (
          <p className={styles.aide}>{t('regie:oeuvres.editions.aucunExemplaire')}</p>
        )
      ) : (
        <table className={styles.tableau}>
          <thead>
            <tr>
              <th scope="col">{t('oeuvres:champs.numeroInventaire')}</th>
              <th scope="col">{t('oeuvres:champs.titre')}</th>
              <th scope="col">{t('oeuvres:champs.statut')}</th>
            </tr>
          </thead>
          <tbody>
            {exemplaires.map((exemplaire) => (
              <tr key={exemplaire.id}>
                <td>
                  <InventoryNumber value={exemplaire.numero} />
                </td>
                <td>{t(`oeuvres:exemplaires.${exemplaire.type}`)}</td>
                <td>
                  <StatusDot
                    status={couleurDuStatut(exemplaire.statut)}
                    lang={langue}
                    label={t(`oeuvres:statuts.${exemplaire.statut}`)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
